import { useRef, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { DockviewApi, DockviewReadyEvent } from 'dockview';
import { useAppSelector, useAppDispatch } from '../redux/hooks';
import { useLazyGetMeQuery } from '../redux/api/authApi';
import { setProfile } from '../redux/slices/authSlice';
import { setCurrentSite } from '../redux/slices/siteSlice';
import {
  setActiveTab,
  setSelectedComponents,
  updateFilterCriteria,
} from '../redux/slices/uiSlice';
import { windowManager } from '../utils/windowManager';
import { renderPopoutTab } from '../components/PopoutTab';
import {
  saveDockviewLayout,
  loadDockviewLayout,
  saveTabCounter,
  loadTabCounter,
  DOCKVIEW_LAYOUT_KEY,
} from '../utils/dockviewStorage';
import { tabConfigs, getTabTitle, tabToRoute, routeToTab } from '../components/panels/DockviewPanels';

const QA_TAB_TYPES = new Set([
  'qaexecutivesummary',
  'pagelist',
  'questionsummary',
  'nomenclature',
  'crosspage',
  'pagereferences',
]);

export interface UseTabManagementReturn {
  dockviewApi: React.MutableRefObject<DockviewApi | null>;
  onReady: (event: DockviewReadyEvent) => void;
  addTab: (tabType: string, popOut?: boolean, position?: 'right' | 'bottom' | 'left' | 'top') => void;
  popOutTab: (tabType: string) => void;
  ensureAsBuiltPanel: () => void;
  handleDragOver: (e: React.DragEvent) => void;
  handleDrop: (e: React.DragEvent) => void;
}

export const useTabManagement = (): UseTabManagementReturn => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const dockviewApi = useRef<DockviewApi | null>(null);
  const [tabCounter, setTabCounterState] = useState(loadTabCounter());
  const hasRestoredLayout = useRef(false);
  const isRestoringLayout = useRef(false);
  const restorationAttempts = useRef(0);
  const maxRestorationAttempts = 3;

  // Get state from Redux
  const activeTab = useAppSelector((state) => state.ui.activeTab);
  const selectedSite = useAppSelector((state) => state.site.currentSite?.site_id ?? null);
  const selectedComponents = useAppSelector((state) => state.ui.selectedComponents);
  const filterCriteria = useAppSelector((state) => state.ui.filterCriteria);
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);

  const [triggerGetMe] = useLazyGetMeQuery();
  const hasFetchedProfileOnMount = useRef(false);

  // Fetch account/profile on mount/refresh if authenticated
  useEffect(() => {
    if (isAuthenticated && !hasFetchedProfileOnMount.current) {
      hasFetchedProfileOnMount.current = true;
      const fetchProfile = async () => {
        try {
          const me = await triggerGetMe().unwrap();
          dispatch(
            setProfile({
              id: me.id ?? null,
              email: me.email ?? null,
              firstName: me.first_name ?? null,
              lastName: me.last_name ?? null,
              phone: me.phone ?? null,
              role: me.role ?? null,
              tenant: me.tenant ?? null,
              active_tenant: (me as any).active_tenant ?? null,
              tenants: (me as any).tenants ?? null,
              activeSite: me.active_site ?? null,
              sessionId: me.session_id ?? null,
              permissions: me.permissions ?? null,
              isActive: typeof me.is_active === 'boolean' ? me.is_active : null,
              is_platform_admin: typeof (me as any).is_platform_admin === 'boolean' ? (me as any).is_platform_admin : null,
              can_upload: typeof me.can_upload === 'boolean' ? me.can_upload : null,
              tenant_can_upload: typeof me.tenant_can_upload === 'boolean' ? me.tenant_can_upload : null,
            })
          );
        } catch (error) {
          console.error('Failed to fetch profile on refresh:', error);
        }
      };
      fetchProfile();
    }
    if (!isAuthenticated) {
      hasFetchedProfileOnMount.current = false;
    }
  }, [isAuthenticated, dispatch, triggerGetMe]);

  // Clear active tab on logout
  useEffect(() => {
    if (!isAuthenticated && activeTab) {
      dispatch(setActiveTab(null));
    }
  }, [isAuthenticated, activeTab, dispatch]);

  // Create default tab when user logs in
  useEffect(() => {
    if (!isAuthenticated || !dockviewApi.current) {
      return;
    }

    const existingPanels = Array.from(dockviewApi.current.panels.values());
    if (existingPanels.length === 0 && !hasRestoredLayout.current && !isRestoringLayout.current) {
      const route = routeToTab[location.pathname] || 'sitedetails';
      addTab(route, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Sync route with active tab
  useEffect(() => {
    if (!isAuthenticated || !dockviewApi.current) {
      return;
    }

    const existingPanels = Array.from(dockviewApi.current.panels.values());

    if (hasRestoredLayout.current && existingPanels.length > 0) {
      return;
    }

    if (existingPanels.length > 0 && !hasRestoredLayout.current) {
      return;
    }

    if (hasRestoredLayout.current && existingPanels.length === 0) {
      hasRestoredLayout.current = false;
      isRestoringLayout.current = false;
    }

    const route = routeToTab[location.pathname];
    if (route) {
      const existingTab = existingPanels.find(p => {
        const panelTabType = p.id.split('-')[0];
        return panelTabType === route;
      });

      if (existingTab) {
        dispatch(setActiveTab(existingTab.id));
      } else {
        addTab(route, false);
      }
    } else {
      const existingTab = existingPanels.find(p => {
        const panelTabType = p.id.split('-')[0];
        return panelTabType === 'sitedetails';
      });

      if (!existingTab) {
        addTab('sitedetails', false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isAuthenticated]);

  // Sync tab changes to route
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    if (activeTab) {
      const tabId = activeTab.split('-')[0];
      const route = tabToRoute[tabId];
      if (route && location.pathname !== route) {
        navigate(route, { replace: true });
      }
    }
  }, [activeTab, navigate, location.pathname, isAuthenticated]);

  // Cross-window communication for popouts
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    const state = { activeTab, selectedSite, selectedComponents, filterCriteria };

    const handlePopoutMessage = (data: any) => {
      switch (data.type) {
        case 'popout-ready':
          const targetWindow = windowManager.getOpenWindows()
            .find(w => w.id === data.windowId)?.window;
          if (targetWindow) {
            renderPopoutTab(data.tabType, getTabTitle(data.tabType), data.windowId, targetWindow);
            windowManager.sendToWindow(data.windowId, { state });
          }
          break;
        case 'state-update':
          const newState = data.state;
          if (newState.selectedSite !== selectedSite) {
            dispatch(setCurrentSite(newState.selectedSite));
          }
          if (JSON.stringify(newState.selectedComponents) !== JSON.stringify(selectedComponents)) {
            dispatch(setSelectedComponents(newState.selectedComponents));
          }
          if (JSON.stringify(newState.filterCriteria) !== JSON.stringify(filterCriteria)) {
            dispatch(updateFilterCriteria(newState.filterCriteria));
          }
          break;
        case 'close-popout':
          windowManager.closeWindow(data.windowId);
          break;
      }
    };

    windowManager.onMessage(handlePopoutMessage);

    return () => {
      // No offMessage helper yet
    };
  }, [activeTab, selectedSite, selectedComponents, filterCriteria, dispatch, isAuthenticated]);

  // Sync state to all popout windows
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    const state = { activeTab, selectedSite, selectedComponents, filterCriteria };
    windowManager.sendToAllWindows({ state });
  }, [activeTab, selectedSite, selectedComponents, filterCriteria, isAuthenticated]);

  // Listen for custom tab open events
  useEffect(() => {
    const handleOpenTab = (event: CustomEvent<{ tabType: string }>) => {
      if (event.detail?.tabType) {
        addTab(event.detail.tabType);
      }
    };

    window.addEventListener('openTab', handleOpenTab as EventListener);
    return () => {
      window.removeEventListener('openTab', handleOpenTab as EventListener);
    };
  }, []);

  // Save layout before page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (dockviewApi.current) {
        saveDockviewLayout(dockviewApi.current);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const ensureAsBuiltPanel = useCallback(() => {
    if (!dockviewApi.current) return;

    const existingPanels = Array.from(dockviewApi.current.panels.values());
    const hasAsBuilt = existingPanels.some(p => p.id.startsWith('asbuilt-'));
    if (hasAsBuilt) return;

    const config = tabConfigs['asbuilt'];
    if (!config) return;

    // Remember the currently active panel so we can re-activate it after
    const previousActivePanel = dockviewApi.current.activePanel;

    const id = `asbuilt-${tabCounter}`;
    const newCounter = tabCounter + 1;
    setTabCounterState(newCounter);
    saveTabCounter(newCounter);

    if (dockviewApi.current.activeGroup) {
      const newGroup = dockviewApi.current.addGroup({
        direction: 'right',
        referenceGroup: dockviewApi.current.activeGroup,
      });

      dockviewApi.current.addPanel({
        id,
        component: config.component,
        title: config.title,
        position: { referenceGroup: newGroup },
      });
    } else {
      dockviewApi.current.addPanel({
        id,
        component: config.component,
        title: config.title,
      });
    }

    // Re-activate the QA panel so new tabs open in that group, not the as-built group
    if (previousActivePanel?.api) {
      previousActivePanel.api.setActive();
    }

    if (dockviewApi.current) {
      saveDockviewLayout(dockviewApi.current);
    }
  }, [tabCounter]);

  // Ensure as-built panel is open when navigateToAsBuiltPage is dispatched
  useEffect(() => {
    const handleNavigateToAsBuilt = () => {
      ensureAsBuiltPanel();
    };

    window.addEventListener('navigateToAsBuiltPage', handleNavigateToAsBuilt as EventListener);
    return () => {
      window.removeEventListener('navigateToAsBuiltPage', handleNavigateToAsBuilt as EventListener);
    };
  }, [ensureAsBuiltPanel]);

  const addTab = useCallback((tabType: string, popOut: boolean = false, position?: 'right' | 'bottom' | 'left' | 'top') => {
    if (!isAuthenticated) {
      return;
    }
    if (popOut) {
      try {
        const title = getTabTitle(tabType);
        windowManager.openPopout(tabType, title);
      } catch (error) {
        console.error('Failed to open pop-out window:', error);
        addTab(tabType, false);
      }
      return;
    }

    if (!dockviewApi.current) return;

    if (hasRestoredLayout.current || isRestoringLayout.current) {
      const existingPanels = Array.from(dockviewApi.current.panels.values());

      const existingTab = existingPanels.find(p => {
        const panelTabType = p.id.split('-')[0];
        return panelTabType === tabType;
      });

      if (existingTab) {
        try {
          const panel = dockviewApi.current.getPanel(existingTab.id);
          if (panel && panel.api) {
            panel.api.setActive();
          }
        } catch (error) {
          console.warn('Failed to activate existing panel:', error);
        }
        dispatch(setActiveTab(existingTab.id));
        if (QA_TAB_TYPES.has(tabType)) {
          setTimeout(() => ensureAsBuiltPanel(), 0);
        }
        const route = tabToRoute[tabType];
        if (route) {
          navigate(route, { replace: true });
        }
        return;
      }

      if (isRestoringLayout.current && existingPanels.length === 0) {
        setTimeout(() => {
          const panelsAfterWait = Array.from(dockviewApi.current?.panels.values() || []);
          const tabAfterWait = panelsAfterWait.find(p => {
            const panelTabType = p.id.split('-')[0];
            return panelTabType === tabType;
          });
          if (tabAfterWait) {
            if (tabAfterWait.api) {
              tabAfterWait.api.setActive();
            }
            dispatch(setActiveTab(tabAfterWait.id));
            const route = tabToRoute[tabType];
            if (route) {
              navigate(route, { replace: true });
            }
            return;
          }
        }, 200);
        return;
      }
    }

    const existingPanels = Array.from(dockviewApi.current.panels.values());
    const existingTab = existingPanels.find(p => {
      const panelTabType = p.id.split('-')[0];
      return panelTabType === tabType;
    });

    if (existingTab) {
      try {
        const panel = dockviewApi.current.getPanel(existingTab.id);
        if (panel && panel.api) {
          panel.api.setActive();
        }
      } catch (error) {
        console.warn('Failed to activate existing panel:', error);
      }
      dispatch(setActiveTab(existingTab.id));
      if (QA_TAB_TYPES.has(tabType)) {
        setTimeout(() => ensureAsBuiltPanel(), 0);
      }
      const route = tabToRoute[tabType];
      if (route) {
        navigate(route, { replace: true });
      }
      return;
    }

    const config = tabConfigs[tabType];
    if (!config) return;

    const id = `${tabType}-${tabCounter}`;
    const newCounter = tabCounter + 1;
    setTabCounterState(newCounter);
    saveTabCounter(newCounter);

    if (position && dockviewApi.current.activeGroup) {
      const newGroup = dockviewApi.current.addGroup({
        direction: position,
        referenceGroup: dockviewApi.current.activeGroup,
      });

      dockviewApi.current.addPanel({
        id,
        component: config.component,
        title: config.title,
        position: { referenceGroup: newGroup },
      });
    } else {
      dockviewApi.current.addPanel({
        id,
        component: config.component,
        title: config.title,
      });
    }

    dispatch(setActiveTab(id));
    if (QA_TAB_TYPES.has(tabType)) {
      setTimeout(() => ensureAsBuiltPanel(), 0);
    }

    if (dockviewApi.current) {
      saveDockviewLayout(dockviewApi.current);
    }

    const route = tabToRoute[tabType];
    if (route) {
      navigate(route, { replace: true });
    }
  }, [isAuthenticated, tabCounter, navigate, dispatch, ensureAsBuiltPanel]);

  const popOutTab = useCallback((tabType: string) => {
    addTab(tabType, true);
  }, [addTab]);

  const onReady = useCallback((event: DockviewReadyEvent) => {
    dockviewApi.current = event.api;

    if (!isAuthenticated) {
      return;
    }

    event.api.onDidAddPanel(() => {
      if (isRestoringLayout.current) {
        return;
      }
      if (dockviewApi.current) {
        saveDockviewLayout(dockviewApi.current);
      }
    });

    event.api.onDidRemovePanel(() => {
      if (isRestoringLayout.current) {
        return;
      }
      if (dockviewApi.current) {
        saveDockviewLayout(dockviewApi.current);
      }
    });

    event.api.onDidActivePanelChange(() => {
      if (isRestoringLayout.current) {
        return;
      }
      if (dockviewApi.current) {
        saveDockviewLayout(dockviewApi.current);
        const activePanel = dockviewApi.current.activePanel;
        if (activePanel) {
          dispatch(setActiveTab(activePanel.id));
        }
      }
    });

    const currentPanels = Array.from(event.api.panels.values());
    if (hasRestoredLayout.current && currentPanels.length === 0 && restorationAttempts.current < maxRestorationAttempts) {
      hasRestoredLayout.current = false;
      restorationAttempts.current++;
    }

    const savedLayout = loadDockviewLayout();
    if (savedLayout && !hasRestoredLayout.current) {
      try {
        hasRestoredLayout.current = true;
        isRestoringLayout.current = true;

        event.api.fromJSON(savedLayout);

        const panelsAfterRestore = Array.from(event.api.panels.values());

        if (panelsAfterRestore.length > 0) {
          setTimeout(() => {
            isRestoringLayout.current = false;

            const panels = Array.from(event.api.panels.values());

            let maxCounter = 0;
            panels.forEach(panel => {
              const parts = panel.id.split('-');
              if (parts.length > 1) {
                const counter = parseInt(parts[parts.length - 1], 10);
                if (!isNaN(counter) && counter >= maxCounter) {
                  maxCounter = counter + 1;
                }
              }
            });

            if (maxCounter > tabCounter) {
              saveTabCounter(maxCounter);
            }

            const activePanel = event.api.activePanel;
            if (activePanel) {
              if (activePanel.api) {
                activePanel.api.setActive();
              }
              const tabId = activePanel.id.split('-')[0];
              const route = tabToRoute[tabId];
              if (route && location.pathname !== route) {
                navigate(route, { replace: true });
              }
              setTimeout(() => {
                if (maxCounter > tabCounter) {
                  setTabCounterState(maxCounter);
                }
                dispatch(setActiveTab(activePanel.id));
              }, 50);
            } else {
              const firstPanel = panels[0];
              if (firstPanel && firstPanel.api) {
                firstPanel.api.setActive();
                const tabId = firstPanel.id.split('-')[0];
                const route = tabToRoute[tabId];
                if (route && location.pathname !== route) {
                  navigate(route, { replace: true });
                }
                setTimeout(() => {
                  if (maxCounter > tabCounter) {
                    setTabCounterState(maxCounter);
                  }
                  dispatch(setActiveTab(firstPanel.id));
                }, 50);
              }
            }
          }, 100);
          return;
        } else {
          isRestoringLayout.current = false;
          hasRestoredLayout.current = false;
          localStorage.removeItem(DOCKVIEW_LAYOUT_KEY);
        }
      } catch (error) {
        console.error('Failed to restore dockview layout:', error);
        localStorage.removeItem(DOCKVIEW_LAYOUT_KEY);
        hasRestoredLayout.current = false;
      }
    }

    const route = location.pathname;
    const tabType = routeToTab[route] || 'home';

    const existingPanels = Array.from(event.api.panels.values());
    const existingTab = existingPanels.find(p => {
      const panelTabType = p.id.split('-')[0];
      return panelTabType === tabType;
    });

    if (!existingTab) {
      if (hasRestoredLayout.current && existingPanels.length === 0) {
        hasRestoredLayout.current = false;
        isRestoringLayout.current = false;
      }
      addTab(tabType);
    } else {
      dispatch(setActiveTab(existingTab.id));
      const mappedRoute = tabToRoute[tabType];
      if (mappedRoute && location.pathname !== mappedRoute) {
        navigate(mappedRoute, { replace: true });
      }
    }
  }, [isAuthenticated, tabCounter, location.pathname, navigate, dispatch, addTab]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    const hasTabId = e.dataTransfer.types.includes('application/tab-id');
    if (hasTabId) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    const tabId = e.dataTransfer.getData('application/tab-id');
    if (tabId) {
      e.preventDefault();
      e.stopPropagation();

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const mouseX = e.clientX;
      const mouseY = e.clientY;

      const rightThreshold = viewportWidth * 0.75;
      const bottomThreshold = viewportHeight * 0.75;
      const leftThreshold = viewportWidth * 0.25;
      const topThreshold = viewportHeight * 0.25;

      let position: 'right' | 'bottom' | 'left' | 'top' | undefined;

      if (mouseX > rightThreshold) {
        position = 'right';
      } else if (mouseX < leftThreshold) {
        position = 'left';
      } else if (mouseY > bottomThreshold) {
        position = 'bottom';
      } else if (mouseY < topThreshold) {
        position = 'top';
      }

      addTab(tabId, false, position);
    }
  }, [addTab]);

  return {
    dockviewApi,
    onReady,
    addTab,
    popOutTab,
    ensureAsBuiltPanel,
    handleDragOver,
    handleDrop,
  };
};
